import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Drawer, Empty, Space, Spin, Tag } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  CalendarDayDetail,
  CalendarDayEntry,
  CalendarDayTask,
  normaliseCalendarDayDetail,
  siteDailyApis,
} from "../../../../api/SiteDaily";
import { DayApprovalActions, useDayApprovalStates } from "../../dailyApprovals/DayApprovalBar";
import { advanceText, stateLabel } from "./calendarModel";

// One day of the Execution Calendar: what each section entered, what is still
// missing, and the actions that fix it — all on that date.

const TaskList: React.FC<{ tasks: CalendarDayTask[]; missing?: boolean; unit: string }> = ({ tasks, missing, unit }) => (
  <ul className="pms-cal-panel-list">
    {tasks.map((task) => (
      <li key={task.taskId} className={missing ? "missing" : ""}>
        <span>
          <b>{task.taskSequenceId}</b> {task.taskName}
          {task.activityName && <span className="pms-cal-sub"> · {task.activityName}</span>}
        </span>
        {unit && (
          <span className="pms-cal-mono">
            {missing ? `${task.lineCount} ${unit}` : `${task.recordedCount} of ${task.lineCount} ${unit}`}
          </span>
        )}
      </li>
    ))}
  </ul>
);

const CalendarDayDrawer: React.FC<{
  open: boolean;
  projectId: number;
  date: string | null;
  phaseId?: number;
  activityId?: number;
  onClose: () => void;
  onAddExecution: (date: string) => void;
  onEditEntry: (entry: CalendarDayEntry, date: string) => void;
  onOpenSheet: (section: "material" | "mechanical", date: string) => void;
  onChanged: () => void;
}> = ({ open, projectId, date, phaseId, activityId, onClose, onAddExecution, onEditEntry, onOpenSheet, onChanged }) => {
  const [detail, setDetail] = useState<CalendarDayDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  const approvals = useDayApprovalStates(open && date ? projectId : undefined, date || "", date || "", tick);

  const load = useCallback(() => {
    if (!open || !date || !projectId) return;
    setLoading(true);
    siteDailyApis
      .calendarDay(projectId, { date, phaseId, activityId })
      .then((res) => {
        if (res?.status !== true) {
          setError(res?.message || "Could not load the day");
          return;
        }
        setError("");
        setDetail(normaliseCalendarDayDetail(res.data));
      })
      .finally(() => setLoading(false));
  }, [open, date, projectId, phaseId, activityId]);

  useEffect(() => {
    setDetail(null);
    load();
  }, [load, tick]);

  const day = detail?.day;
  const future = !!date && dayjs(date).isAfter(dayjs(), "day");

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      title={date ? `${dayjs(date).format("DD-MMM-YYYY")} · ${dayjs(date).format("dddd")}` : ""}
      destroyOnClose
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      {loading && !detail ? (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Spin />
        </div>
      ) : detail && day && date ? (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <div className="pms-cal-panel-head">
            {stateLabel(day) && <span className={`pms-cal-state-pill pms-cal-${day.state}`}>{stateLabel(day)}</span>}
            {day.holidayName && <Tag>{day.holidayName}</Tag>}
            {!day.isWorkingDay && !day.holidayName && <Tag>Weekly off-day</Tag>}
          </div>
          {!future && (
            <DayApprovalActions
              projectId={projectId}
              date={date}
              state={approvals.states.get(date)}
              onChanged={() => {
                setTick((t) => t + 1);
                onChanged();
              }}
            />
          )}

          <section className="pms-cal-panel-section">
            <div className="pms-cal-panel-title">
              <span>Civil</span>
              {!future && (
                <Button size="small" icon={<PlusOutlined />} onClick={() => onAddExecution(date)}>
                  Add execution
                </Button>
              )}
            </div>
            {!detail.entries.length && <div className="pms-cal-empty">No Civil entry on this day.</div>}
            <ul className="pms-cal-panel-list">
              {detail.entries.map((entry) => (
                <li key={entry.entryId}>
                  <span>
                    <b>{entry.activityName}</b>
                    <div className="pms-cal-sub">
                      {entry.shiftName || "Whole day"}
                      {entry.startTime && entry.endTime ? ` · ${entry.startTime}–${entry.endTime}` : ""}
                      {entry.phaseName ? ` · ${entry.phaseName}` : ""}
                    </div>
                    {entry.advance.length > 0 && <div className="pms-cal-mono">{advanceText(entry.advance, 5)}</div>}
                  </span>
                  <span className="pms-cal-panel-side">
                    <span className="pms-cal-mono">{entry.boqLineCount} BOQ lines</span>
                    <Button size="small" type="link" icon={<EditOutlined />} onClick={() => onEditEntry(entry, date)}>
                      Open
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
            {detail.facesNotCaptured.length > 0 && (
              <>
                <div className="pms-cal-missing-title">Tracked faces with no entry</div>
                <ul className="pms-cal-panel-list">
                  {detail.facesNotCaptured.map((face) => (
                    <li key={face.activityId} className="missing">
                      <span>{face.activityName}</span>
                      <span className="pms-cal-mono">{face.trackedTaskCount} tracked task{face.trackedTaskCount === 1 ? "" : "s"}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {detail.trackedNotCaptured.length > 0 && (
              <>
                <div className="pms-cal-missing-title">Tracked tasks with no Civil line</div>
                <TaskList tasks={detail.trackedNotCaptured} missing unit="" />
              </>
            )}
            {!detail.monitoringConfigured && detail.entries.length === 0 && (
              <div className="pms-cal-sub">Task Monitoring is not set up, so no faces are expected on a given day.</div>
            )}
          </section>

          <section className="pms-cal-panel-section">
            <div className="pms-cal-panel-title">
              <span>Material-Stores</span>
              {!future && (
                <Button size="small" onClick={() => onOpenSheet("material", date)}>
                  Open Material-Stores
                </Button>
              )}
            </div>
            {!detail.materialRecorded.length && !detail.materialMissing.length && (
              <div className="pms-cal-empty">No task produced with material lines on this day.</div>
            )}
            {detail.materialRecorded.length > 0 && <TaskList tasks={detail.materialRecorded} unit="materials" />}
            {detail.materialMissing.length > 0 && (
              <>
                <div className="pms-cal-missing-title">Produced tasks with no issue</div>
                <TaskList tasks={detail.materialMissing} missing unit="material lines" />
              </>
            )}
          </section>

          <section className="pms-cal-panel-section">
            <div className="pms-cal-panel-title">
              <span>Mechanical</span>
              {!future && (
                <Button size="small" onClick={() => onOpenSheet("mechanical", date)}>
                  Open Mechanical
                </Button>
              )}
            </div>
            {!detail.mechanicalRecorded.length && !detail.mechanicalMissing.length && (
              <div className="pms-cal-empty">No task produced with deployed machines on this day.</div>
            )}
            {detail.mechanicalRecorded.length > 0 && <TaskList tasks={detail.mechanicalRecorded} unit="machines" />}
            {detail.mechanicalMissing.length > 0 && (
              <>
                <div className="pms-cal-missing-title">Produced tasks with no machine log</div>
                <TaskList tasks={detail.mechanicalMissing} missing unit="deployed machines" />
              </>
            )}
          </section>
        </Space>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Drawer>
  );
};

export default CalendarDayDrawer;
